import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { CircularProgress } from '@mui/material';
import { Checkbox } from '../../components/UI/Checkbox/Checkbox';
import { Button } from '../../components/UI/Button/Button';
import { useApi } from '../../hooks/useApi';
import { useStores } from '../../hooks/useStore';
import { Course, CourseModule, Lesson } from '../../models/courses';
import { AssignmentQuestion, LessonAssignment } from '../../models/assignments';
import {
  AssignmentCreateRequest,
  AssignmentUpdateRequest,
} from '../../api/assignments/dto/AssignmentRequests.dto';
import { QuestionRequest } from '../../api/assignments/dto/QuestionRequests.dto';
import QuestionCard from './components/assignment/QuestionCard';
import SortableQuestionList from './components/assignment/SortableQuestionList';
import styles from './AssignmentEditor.module.css';

interface AssignmentEditorProps {
  course: Course;
  module: CourseModule;
  lesson: Lesson;
  onBack: () => void;
  onBackToCourses: () => void;
  onBackToModules: () => void;
}

// Форма отказа interceptor'а ApiClient при не-2xx ответе.
function hasCode(e: unknown): e is { code: number; error?: string } {
  return typeof e === 'object' && e !== null && 'code' in e;
}

// Пустое поле лимита попыток трактуем как "без лимита" — на бэке это 0/NULL.
function parseAttempts(value: string): number {
  const trimmed = value.trim();
  if (trimmed === '') {
    return 0;
  }
  const parsed = parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

const AssignmentEditor: React.FC<AssignmentEditorProps> = observer(
  ({ course, module, lesson, onBack, onBackToCourses, onBackToModules }) => {
    const api = useApi();
    const store = useStores();

    const [loaded, setLoaded] = useState<boolean>(false);
    const [assignment, setAssignment] = useState<LessonAssignment | null>(null);
    const [questions, setQuestions] = useState<AssignmentQuestion[]>([]);
    const [draft, setDraft] = useState<boolean>(false);
    // Блокирует повторные клики мутаций, пока не завершится текущая (запрос + перечитывание).
    const [busy, setBusy] = useState<boolean>(false);
    // Счётчик load()-вызовов: устаревший ответ (пришёл после более нового) игнорируется.
    const loadSeq = useRef(0);
    // id последнего задания, на которое реально синхронизировалась мета-форма.
    const syncedAssignmentId = useRef<string | null>(null);

    // Форма создания задания (пока assignment === null).
    const [cTitle, setCTitle] = useState<string>('');
    const [cDescription, setCDescription] = useState<string>('');
    const [cAutoAccept, setCAutoAccept] = useState<boolean>(false);
    const [cMaxAttempts, setCMaxAttempts] = useState<string>('');

    // Форма редактирования мета-данных существующего задания.
    const [mTitle, setMTitle] = useState<string>('');
    const [mDescription, setMDescription] = useState<string>('');
    const [mAutoAccept, setMAutoAccept] = useState<boolean>(false);
    const [mMaxAttempts, setMMaxAttempts] = useState<string>('');

    const load = async (): Promise<void> => {
      const seq = ++loadSeq.current;
      try {
        store.notificationsStore.showPreloader();
        const a = await api.assignments.getByLesson(lesson.id);
        // Устаревший ответ (более новый load() уже запущен) — игнорируем.
        if (seq !== loadSeq.current) {
          return;
        }
        setAssignment(a);
        setLoaded(true);
      } catch (e) {
        if (seq !== loadSeq.current) {
          return;
        }
        store.notificationsStore.setError(
          hasCode(e) && e.error ? e.error : 'Ошибка при загрузке задания',
        );
      } finally {
        store.notificationsStore.hidePreloader();
      }
    };

    useEffect(() => {
      load();
    }, [lesson.id]);

    // Вопросы синхронизируются на каждую перезагрузку; мета-форма — только
    // когда "чистая" или сменилось само задание (иначе мутации вопросов/
    // публикации стирали бы не сохранённый ввод title/description).
    useEffect(() => {
      if (assignment) {
        const idChanged = syncedAssignmentId.current !== assignment.id;
        const formIsClean =
          mTitle === assignment.title &&
          mDescription === assignment.description &&
          mAutoAccept === assignment.auto_accept &&
          parseAttempts(mMaxAttempts) === (assignment.max_attempts ?? 0);
        if (idChanged || formIsClean) {
          setMTitle(assignment.title);
          setMDescription(assignment.description);
          setMAutoAccept(assignment.auto_accept);
          setMMaxAttempts(assignment.max_attempts === null ? '' : String(assignment.max_attempts));
        }
        syncedAssignmentId.current = assignment.id;
      } else {
        syncedAssignmentId.current = null;
      }
      const sorted = [...(assignment?.questions ?? [])].sort(
        (a, b) => a.sort_order - b.sort_order,
      );
      setQuestions(sorted);
    }, [assignment]);

    const handleCreate = async (): Promise<void> => {
      if (!cTitle.trim()) {
        store.notificationsStore.setError('Название задания обязательно');
        return;
      }

      const req: AssignmentCreateRequest = {
        title: cTitle,
        description: cDescription,
        auto_accept: cAutoAccept,
        max_attempts: parseAttempts(cMaxAttempts),
        is_published: false,
      };

      setBusy(true);
      try {
        await api.assignments.create(lesson.id, req);
        store.notificationsStore.setNotifications('Задание создано');
        await load();
      } catch (e) {
        if (hasCode(e) && e.code === 409) {
          // Двойной клик по кнопке — задание уже создано, просто перечитываем.
          await load();
          return;
        }
        store.notificationsStore.setError(
          hasCode(e) && e.error ? e.error : 'Ошибка при создании задания',
        );
      } finally {
        setBusy(false);
      }
    };

    // Diff считается только по полям, реально изменённым пользователем —
    // update() трактует отсутствие поля как "не менять".
    const buildDiff = (): AssignmentUpdateRequest => {
      if (!assignment) {
        return {};
      }
      const diff: AssignmentUpdateRequest = {};
      if (mTitle !== assignment.title) {
        diff.title = mTitle;
      }
      if (mDescription !== assignment.description) {
        diff.description = mDescription;
      }
      if (mAutoAccept !== assignment.auto_accept) {
        diff.auto_accept = mAutoAccept;
      }
      const currentAttempts = parseAttempts(mMaxAttempts);
      const originalAttempts = assignment.max_attempts ?? 0;
      if (currentAttempts !== originalAttempts) {
        diff.max_attempts = currentAttempts;
      }
      return diff;
    };

    const diff = buildDiff();
    const hasChanges = Object.keys(diff).length > 0;

    const handleSaveMeta = async (): Promise<void> => {
      if (!assignment || !hasChanges) {
        return;
      }
      setBusy(true);
      try {
        await api.assignments.update(assignment.id, diff);
        store.notificationsStore.setNotifications('Задание сохранено');
        await load();
      } catch (e) {
        store.notificationsStore.setError(
          hasCode(e) && e.error ? e.error : 'Ошибка при сохранении задания',
        );
      } finally {
        setBusy(false);
      }
    };

    const handleTogglePublished = async (checked: boolean): Promise<void> => {
      if (!assignment) {
        return;
      }
      setBusy(true);
      try {
        await api.assignments.update(assignment.id, { is_published: checked });
        await load();
      } catch (e) {
        store.notificationsStore.setError(
          hasCode(e) && e.error ? e.error : 'Ошибка при сохранении задания',
        );
      } finally {
        setBusy(false);
      }
    };

    const handleDeleteAssignment = async (): Promise<void> => {
      if (!assignment) {
        return;
      }
      if (!window.confirm('Удалить задание вместе с вопросами?')) {
        return;
      }
      setBusy(true);
      try {
        await api.assignments.delete(assignment.id);
        store.notificationsStore.setNotifications('Задание удалено');
        await load();
      } catch (e) {
        store.notificationsStore.setError(
          hasCode(e) && e.error ? e.error : 'Ошибка при удалении задания',
        );
      } finally {
        setBusy(false);
      }
    };

    const handleReorder = async (ids: string[]): Promise<void> => {
      if (!assignment) {
        return;
      }
      const prev = questions;
      const byId = new Map(questions.map((q) => [q.id, q]));
      const reordered = ids
        .map((id) => byId.get(id))
        .filter((q): q is AssignmentQuestion => Boolean(q));
      setQuestions(reordered);
      setBusy(true);
      try {
        await api.assignments.reorderQuestions(assignment.id, { ids });
        // id вариантов и sort_order пересчитываются на бэке — перечитываем.
        await load();
      } catch (e) {
        setQuestions(prev);
        store.notificationsStore.setError(
          hasCode(e) && e.error ? e.error : 'Ошибка при изменении порядка',
        );
      } finally {
        setBusy(false);
      }
    };

    // Мутации вопросов пробрасывают ошибку дальше — иначе QuestionCard
    // посчитает сохранение успешным и сбросит индикатор несохранённых изменений.
    const addQuestion = async (req: QuestionRequest): Promise<void> => {
      if (!assignment) {
        return;
      }
      setBusy(true);
      try {
        await api.assignments.addQuestion(assignment.id, req);
        setDraft(false);
        await load();
      } catch (e) {
        store.notificationsStore.setError(
          hasCode(e) && e.error ? e.error : 'Ошибка при сохранении вопроса',
        );
        throw e;
      } finally {
        setBusy(false);
      }
    };

    const saveQuestion = async (questionId: string, req: QuestionRequest): Promise<void> => {
      setBusy(true);
      try {
        await api.assignments.updateQuestion(questionId, req);
        await load();
      } catch (e) {
        store.notificationsStore.setError(
          hasCode(e) && e.error ? e.error : 'Ошибка при сохранении вопроса',
        );
        throw e;
      } finally {
        setBusy(false);
      }
    };

    const deleteQuestion = async (questionId: string): Promise<void> => {
      setBusy(true);
      try {
        await api.assignments.deleteQuestion(questionId);
        await load();
      } catch (e) {
        store.notificationsStore.setError(
          hasCode(e) && e.error ? e.error : 'Ошибка при сохранении вопроса',
        );
        throw e;
      } finally {
        setBusy(false);
      }
    };

    const breadcrumb = (
      <div className={styles.breadcrumb}>
        <span className={styles.crumb} onClick={onBackToCourses}>Курсы</span>
        <span className={styles.crumbSep}>›</span>
        <span className={styles.crumb} onClick={onBackToModules}>{course.title}</span>
        <span className={styles.crumbSep}>›</span>
        <span className={styles.crumb} onClick={onBack}>{module.title}</span>
        <span className={styles.crumbSep}>›</span>
        <span className={styles.crumbCurrent}>{lesson.title}</span>
      </div>
    );

    if (!loaded) {
      return (
        <div className={styles.editor}>
          {breadcrumb}
          <div className={styles.loading}>
            <CircularProgress />
          </div>
        </div>
      );
    }

    if (assignment === null) {
      return (
        <div className={styles.editor}>
          {breadcrumb}
          <div className={styles.meta}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Название задания</span>
              <input className={styles.titleInput} aria-label="Название задания" value={cTitle}
                onChange={(e) => setCTitle(e.target.value)} placeholder="Введите название задания" />
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Описание</span>
              <textarea className={styles.textArea} aria-label="Описание" value={cDescription}
                onChange={(e) => setCDescription(e.target.value)} />
            </div>
            <div className={styles.metaRow}>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Лимит попыток</span>
                <input className={styles.durationInput} type="number" min={0} aria-label="Лимит попыток"
                  value={cMaxAttempts} onChange={(e) => setCMaxAttempts(e.target.value)} />
              </div>
              <div className={styles.toggleWrap}>
                <Checkbox label="Авто-зачёт (принимать без проверки)" checked={cAutoAccept}
                  onChange={(e) => setCAutoAccept(e.target.checked)} />
              </div>
            </div>
            <span className={styles.hint}>Публикация станет доступна после добавления вопросов</span>
            <div className={styles.actions}>
              <Button appearance="auth" onClick={handleCreate} disabled={busy}>Создать задание</Button>
            </div>
          </div>

          <div className={styles.actionBar}>
            <button type="button" className={styles.cancelBtn} onClick={onBack}>Назад</button>
          </div>
        </div>
      );
    }

    const noQuestions = questions.length === 0;

    return (
      <div className={styles.editor}>
        {breadcrumb}

        <div className={styles.meta}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Название задания</span>
            <input className={styles.titleInput} aria-label="Название задания" value={mTitle}
              onChange={(e) => setMTitle(e.target.value)} placeholder="Введите название задания" />
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Описание</span>
            <textarea className={styles.textArea} aria-label="Описание" value={mDescription}
              onChange={(e) => setMDescription(e.target.value)} />
          </div>
          <div className={styles.metaRow}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Лимит попыток</span>
              <input className={styles.durationInput} type="number" min={0} aria-label="Лимит попыток"
                value={mMaxAttempts} onChange={(e) => setMMaxAttempts(e.target.value)} />
            </div>
            <div className={styles.toggleWrap}>
              <Checkbox label="Авто-зачёт (принимать без проверки)" checked={mAutoAccept}
                onChange={(e) => setMAutoAccept(e.target.checked)} />
            </div>
          </div>

          <div className={styles.versionRow}>
            <span className={styles.badge}>Версия {assignment.version}</span>
            <span className={styles.hint}>Изменение вопросов повышает версию; сданные работы хранят снимок вопросов.</span>
          </div>

          <div className={styles.toggleWrap}>
            <Checkbox label="Опубликовано" aria-label="Опубликовано" checked={assignment.is_published}
              disabled={noQuestions || busy} onChange={(e) => handleTogglePublished(e.target.checked)}
              title={noQuestions ? 'Добавьте хотя бы один вопрос' : undefined} />
          </div>

          {assignment.is_published && noQuestions && (
            <div role="alert" className={styles.alert}>
              Задание опубликовано без вопросов — снимите публикацию или добавьте вопрос
            </div>
          )}

          <div className={styles.actions}>
            <Button appearance="auth" onClick={handleSaveMeta} disabled={!hasChanges || busy}>Сохранить</Button>
            <Button appearance="link" onClick={handleDeleteAssignment} disabled={busy}>Удалить задание</Button>
          </div>
        </div>

        <div className={styles.sectionLabel}>Вопросы</div>

        {questions.length === 0 && !draft ? (
          <div className={styles.empty}>Пока нет ни одного вопроса</div>
        ) : (
          <div className={styles.questions}>
            <SortableQuestionList
              questions={questions}
              disabled={busy}
              onReorder={handleReorder}
              renderQuestion={(q) => (
                <QuestionCard
                  question={q}
                  index={questions.findIndex((item) => item.id === q.id)}
                  onSave={(req) => saveQuestion(q.id, req)}
                  onDelete={() => deleteQuestion(q.id)}
                  disabled={busy}
                />
              )}
            />
            {draft && (
              <QuestionCard
                question={null}
                index={questions.length}
                onSave={addQuestion}
                onDelete={async () => setDraft(false)}
                onDiscardDraft={() => setDraft(false)}
                disabled={busy}
              />
            )}
          </div>
        )}

        {!draft && (
          <button type="button" className={styles.addBtn} onClick={() => setDraft(true)}>Добавить вопрос</button>
        )}

        <div className={styles.actionBar}>
          <button type="button" className={styles.cancelBtn} onClick={onBack}>Назад</button>
        </div>
      </div>
    );
  },
);

export default AssignmentEditor;
